import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import debounce from 'lodash/debounce';
import {
  Grid,
  Input,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  Typography,
  Paper,
  LinearProgress,
  Tooltip,
  Badge,
  Switch
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import RefreshIcon from '@material-ui/icons/Refresh';
import CloseIcon from '@material-ui/icons/Close';
import SortIcon from '@material-ui/icons/Sort';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import DeleteSweepIcon from '@material-ui/icons/DeleteSweep';
import AmherstAIcon from "assets/images/AmherstA.svg";
import {
  setQueueFilterInput,
  changeQueueSort,
  clearCompletedQueue,
  setShowPostAcquisitions,
} from 'store/actions';
import { LoadingOverlayWrapper } from 'components/layout';
import { QueueBin } from 'components/queue';
import { sendClientLog } from 'services/logService';
import { withRouter } from 'react-router-dom';
import { Constants, getModuleByLocation } from 'utilities/constants';

const QueueContainer = ({
  classes,
  module,
  queueFilterInput,
  setQueueFilterInput,
  queueBinsState,
  isFetchingQueueTxnOppsData,
  changeQueueSort,
  queueSortInput,
  handleDataFetch,
  queueTxnOppsNeedToRefresh,
  filteredTasks,
  clearCompletedQueue,
  handleScroll,
  scroll,
  showPostAcquisitions,
  setShowPostAcquisitions,
  unloadedTaskCount
}) => {
  const filterInputRef = useRef(null);
  const [queueFilterInputLocalState, setQueueFilterInputLocalState] = useState(queueFilterInput);
  const [sortMenuAnchorEl, setSortMenuAnchorEl] = useState(null);

  useEffect(() => {
    // On mount (reopening the queue), set the filter input to the previous queuefilter input
    setQueueFilterInputLocalState(queueFilterInput);
    // Alternatively, if we want to reset the queue when drawer closes, on unmount, set filter to empty
    // return () => {setQueueFilterInput('')}
    sendClientLog('Info', `User opened ${module} queue`);
  }, []);

  // const focusFilterInput = (input, tries) => {
  //   if (tries > 3) {
  //     // console.log('focusFilterInput tries > 3, stopping retries...');
  //     return;
  //   }
  //   setTimeout(() => {
  //     if (input === null) {
  //       // console.log('filterInputRef is null');
  //       focusFilterInput(input, ++tries);
  //     } else if (input === undefined) {
  //       // console.log('filterInputRef is undefined');
  //       focusFilterInput(input, ++tries);
  //     }
  //     // console.log('focusFilterInput completed');
  //     input.current.focus();
  //     input.current.setSelectionRange(input.current.value.length, input.current.value.length);  // Set cursor to end of current input
  //   }, 100);
  // }
  const handleFilterInputChanged = input => {
    // console.log('handleFilterInputChanged');
    setQueueFilterInputLocalState(input);           // we're keeping a local state copy, so that the UI can immediately reflect the input, and then the queueFilterInput in the store will be debounced to only update once the user finishes typing
    setQueueFilterInput(input);                     // this gets debounce for setQueueFilterInputSaga
  }

  const handleClearCompleted = () => {
    clearCompletedQueue();
  }

  const handleShowPostAcquisitions  = () => {
    const shouldShow = showPostAcquisitions ? false : true
    setShowPostAcquisitions(shouldShow);
    handleDataFetch();
  }

  const clearSearch = () => {
    setQueueFilterInputLocalState('');
    setQueueFilterInput('');
  }

  const handleDataRefresh = () => {
    if (!isFetchingQueueTxnOppsData) {
      handleDataFetch();
      if (module === Constants.Module.POSTACQ) {
        sendClientLog('Info', 'Post-Acq Workflow - User started data fetch - queue refresh button');
      } else {
        sendClientLog('Info', 'Channel Workflow - User started clientTxnOpps data fetch - queue refresh button');
      }
    }
  }

  // Open the sort menu
  const openSortMenu = e => {
    e.stopPropagation();
    setSortMenuAnchorEl(e.currentTarget);
    // setSortWasClicked(true);
  }
  // Close the sort menu
  const closeSortMenu = e => {
    setSortMenuAnchorEl(null);
    // setSortWasClicked(false);
  }
  // On menu click, change the sort order of the menu item and trigger changeQueueSortSaga
  const menuItemClick = e => {
    if (e.currentTarget && e.currentTarget.innerText) {
      if(e.currentTarget.innerText === "Clear"){
        e.stopPropagation();
        changeQueueSort({
          sortInput: null,
          quickChange: 'clear'
        });
      } else {
        changeQueueSort({
          sortInput: e.currentTarget.innerText,
          quickChange: null
        });
      }
    }
  }

  const getFilteredTasks = bin => {
    const tasks = queueFilterInput === '' ? bin.tasks : bin.tasks.filter(task => filteredTasks.includes(task.id));
    return {
      ...bin,
      tasks,
      hasNoSearchResults: queueFilterInput !== '' && bin.tasks.length && !filteredTasks.length
    }
  }

  function capitalizeString(str) {
    if (typeof str !== 'string' || !str) return str;
    return str.replace(str[0], str[0].toUpperCase());
  }

  const sortingIndicator = React.useMemo(() => {
    const sortedFields = queueSortInput.filter(field => field.order);
    return !!sortedFields.length;
  }, [queueSortInput]);

  const isClearCompletedButtonActive = queueBinsState?.allTasks.isAnyTaskCompleted(); // Post-acq my tasks - update isAnyTaskCompleted to correctly count completed post-acq opps (valuation count > 0)

  function getSortingOptions() {
    let sortingOptions = [];
    let sortingHierarchyIndicator = 0;
    let isClearOptionVisible = false;

    queueSortInput.forEach(option => {
      let label;
      let isClearOption = option.label === "clear"; // check if its "Clear" option to apply different styling
      try {
        label = capitalizeString(option.label);
      } catch {
        label = option.label;
      }
      if(option.order) {
        // if option selected, then increase the indicator
        sortingHierarchyIndicator++;
        // if at least one is selected, then make "Clear" option visible
        isClearOptionVisible = true;
      }
      sortingOptions.push(
        isClearOption && !isClearOptionVisible
        ? null
        : <MenuItem
          className={isClearOption ? classes.clearSortingOption : ''}
          key={option.label}
          onClick={menuItemClick}
        >
          {label}
          {option.order === 'asc' ? <ArrowUpwardIcon fontSize='small' /> : option.order === 'desc' ? <ArrowDownwardIcon fontSize='small' /> : null}
          <sup className={classes.sortingIndicator}>{option.order ? sortingHierarchyIndicator : null}</sup>
        </MenuItem>
      )
    });
    return sortingOptions;
  }

  return (
    <>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <Typography variant='subtitle2' className={classes.headerText} color='inherit'>{module === Constants.Module.UNDERWRITE ? 'My Opps' : 'My Tasks'}</Typography>
          <Badge
            badgeContent={queueBinsState ? queueBinsState.allTasks.tasks.length : 0}
            color='primary'
            classes={{ root: classes.badgeRoot, colorPrimary: classes.badgeColorPrimary, badge: classes.badge }}
            max={999}
            showZero
            overlap='rectangular'
          >
            <div />
          </Badge>
          {/* {module === Constants.Module.UNDERWRITE &&
            <Tooltip title={showPostAcquisitions ? `Change to Acquisitions`: `Change to Post-Acquisitions`}>
              <div>
                <Switch
                  checked={showPostAcquisitions}
                  onChange={handleShowPostAcquisitions}
                  className={classes.postAcqSwitch}
                />
              </div>
            </Tooltip>
          } */}
        </div>
        <div className={classes.headerRight}>
          <Tooltip title='Clear Completed'>
            <div>
              <IconButton
                onClick={handleClearCompleted}
                disabled={!isClearCompletedButtonActive}
                size='small'
                color='inherit'
                classes={{ root: classes.iconButton, disabled: classes.headerIconDisabled }}
              >
                <DeleteSweepIcon />
              </IconButton>
            </div>
          </Tooltip>
          <Tooltip title='Refresh Data'>
            <Badge invisible={!queueTxnOppsNeedToRefresh} classes={{ colorPrimary: classes.colorPrimary, badge: classes.refreshBadge }} color='primary' variant="dot" overlap='rectangular'>
              <IconButton onClick={handleDataRefresh} disabled={isFetchingQueueTxnOppsData} size='small' color='inherit' classes={{ root: classes.iconButton, disabled: classes.headerIconDisabled }}>
                <RefreshIcon />
              </IconButton>
            </Badge>
          </Tooltip>
          <Tooltip title='Sort Tasks By'>
            <Badge
              invisible={!sortingIndicator}
              classes={{ colorPrimary: classes.colorPrimary, badge: classes.refreshBadge }}
              color='secondary'
              variant="dot"
              overlap='rectangular'
            >
              <IconButton
                onClick={openSortMenu}
                color='inherit'
                classes={{ root: classes.iconButton, disabled: classes.headerIconDisabled }}
              >
                <SortIcon />
              </IconButton>
            </Badge>
          </Tooltip>
          <Menu
            id='filterMenu'
            anchorEl={sortMenuAnchorEl}
            open={Boolean(sortMenuAnchorEl)}
            onClose={closeSortMenu}
          >
            {getSortingOptions()}
          </Menu>
          {/* <IconButton className={classes.iconButton} onClick={() => setSearchExpanded(!searchExpanded)} color='inherit'>
            <SearchIcon />
          </IconButton> */}
        </div>
      </div>
      {/* <Collapse in={searchExpanded}> */}
      <Grid container alignItems='center'>
        <Paper className={classes.queueSearchPaper}>
          <FormControl fullWidth={true} className={classes.queueFilterInputContainer}>
            <Input inputRef={filterInputRef} type='text' className={classes.queueFilterInput}
              value={queueFilterInputLocalState} onChange={e => handleFilterInputChanged(e.target.value)}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              }
              endAdornment=
              {
                <InputAdornment position='end'>
                  <Tooltip title='Clear Search box'>
                    <IconButton className={classes.iconButton} onClick={clearSearch}>
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              }
            />
          </FormControl>
        </Paper>

      </Grid>
      {isFetchingQueueTxnOppsData === true &&
        <LinearProgress />
      }
      <LoadingOverlayWrapper
        className={classes.loadingOverlay}
        active={isFetchingQueueTxnOppsData}
        // spinner={<LinearProgress />}
        message={`Loading data...`}
      >
        <QueueBin
          module={module}
          bin={queueBinsState && getFilteredTasks(queueBinsState.allTasks)}
          handleScroll={handleScroll}
          scroll={scroll}
          queueFilterInputLocalState={queueFilterInputLocalState}
          unloadedTaskCount={unloadedTaskCount}
        />
      </LoadingOverlayWrapper>
      {queueBinsState && queueBinsState.allTasks.tasks.length > 0 && unloadedTaskCount > 0 &&
        <div className={classes.messageContainer}>
          <Typography variant='body1' className={classes.unloadedTasksText}>{`${unloadedTaskCount} more ${unloadedTaskCount > 1 ? 'tasks require' : 'task requires'} data load`}</Typography>
        </div>
      }
    </>
  )
}

const styles = theme => ({
  queueFilterChipContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  queueSearchPaper: {
    margin: 8,
    flexGrow: 1
  },
  queueFilterInputContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  },
  queueFilterInput: {
    paddingLeft: '6px',
    flexGrow: 2
  },
  textPrimary: {
    fontSize: '.8rem',
    fontWeight: '500',
    margin: '0px 0px 0px 0px',
  },
  textSecondary: {
    fontSize: '.75rem',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  rowContainer: {
    marginTop: '-7px',
  },
  list: {
    paddingTop: '0px',
    paddingBottom: '0px',
  },
  listItem: {
    paddingLeft: '10px',
    paddingRight: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
    borderBottom: '1px solid #bfbfbf',
    borderRight: '1px solid #bfbfbf',
    backgroundColor: '#fff',
  },
  avatar: {
    marginRight: '0px',
  },
  avatarText: {
    fontSize: '1.55rem',
    fontWeight: '500',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  green: {
    backgroundColor: 'green',
  },
  orange: {
    backgroundColor: 'orange',
  },
  inline: {
    display: 'inline'
  },
  spinner: {
    color: '#6798e5',
    animationDuration: '800ms',
    // left: 0,
  },
  chip: {
    margin: '0px',
  },
  queueFilterChip: {
    height: 14,
    color: '#fff',
    borderRadius: 5,
    margin: 1
  },
  loadingOverlay: {
    flexGrow: 1,
    // overflowY: 'auto'
    display: 'flex',
    minHeight: 0
  },
  iconButton: {
    padding: '5px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#3e4350',
    color: '#fff',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    margin: '8px 16px',
  },
  headerText: {
    fontSize: '1.2rem',
    marginRight: 4
  },
  headerRight: {
    //color: '#fff'
    marginRight: 4,
    display: 'flex'
  },
  headerIconDisabled: {
    color: 'rgba(255, 255, 255, 0.2)!important'
  },
  badgeRoot: {
    position: 'relative',
    display: 'inline-flex',
    marginLeft: 18,
    // For correct alignment with the text.
    verticalAlign: 'middle',
  },
  badgeColorPrimary: {
    backgroundColor: '#555d65',
    color: theme.palette.primary.contrastText,
  },
  badge: {
    top: '0px',
  },
  refreshBadge: {
    marginTop: 6,
    marginRight: 6
  },
  colorPrimary: {
    backgroundColor: 'Red', //'#8c8c8c', '#999999'
    color: 'White',
  },
  clearSortingOption: {
    borderTop: '1px solid rgba(0, 0, 0, 0.2)'
  },
  sortingIndicator: {
    fontSize: '12px'
  },
  postAcqSwitch: {
    marginLeft: 14,
    width: 62,
    height: 34,
    padding: 10,
    '& .MuiSwitch-switchBase': {
      margin: 1,
      padding: 0,
      transform: 'translateX(6px)',
      '&.Mui-checked': {
        color: '#fff',
        transform: 'translateX(22px)',
        '& .MuiSwitch-thumb:before': {
          backgroundImage: `url(${AmherstAIcon})`,
          backgroundSize: 22
        },
        '& + .MuiSwitch-track': {
          opacity: 1,
          backgroundColor: theme.palette.mode === 'dark' ? '#8796A5' : '#aab4be',
        },
      },
    },
    '& .MuiSwitch-thumb': {
      backgroundColor: theme.palette.secondary.main,
      width: 32,
      height: 32,
      '&:before': {
        content: "''",
        position: 'absolute',
        width: '100%',
        height: '100%',
        left: 0,
        top: 0,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
            '#fff',
          )}" d="M18 8H8C6.9 8 6 8.9 6 10V16C6 17.11 6.9 18 8 18H18C19.11 18 20 17.11 20 16V10C20 8.9 19.11 8 18 8M14 16H8V14H14V16M18 12H8V10H18V12M22 6H4V22H2V2H4V4H22V6Z"/></svg>')`,
      },
    },
    '& .MuiSwitch-track': {
      opacity: 1,
      backgroundColor: theme.palette.mode === 'dark' ? '#8796A5' : '#aab4be',
      borderRadius: 20 / 2,
    },
  },
  messageContainer: {
    padding: '8px 24px',
    marginTop: -1,
    borderTop: '1px solid #dddddd',
    zIndex: 1
  },
  unloadedTasksText: {
    textAlign: 'center',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    color: '#A7A9AC',
    fontSize: '0.9rem',
    fontStyle: 'italic'
  }
});

const mapStateToProps = (state, ownProps) => {
  const module = getModuleByLocation(ownProps.location);
  const { queueTxnOpps, isFetchingQueueTxnOppsData, queueBinsState, queueFilterInput, queueSortInput, queueTxnOppsNeedToRefresh } =  state.queueState[module];
  const unloadedTaskCount = module === Constants.Module.POSTACQ && queueBinsState && queueTxnOpps?.length ? queueTxnOpps.length - queueBinsState.allTasks.tasks.length : 0;
  return {
    module,
    isFetchingQueueTxnOppsData,
    queueBinsState,
    queueFilterInput,
    queueSortInput,
    queueTxnOppsNeedToRefresh,
    showPostAcquisitions: state.queueState.underwrite.showPostAcquisitions,
    unloadedTaskCount
  }
};
const mapDispatchToProps = (dispatch, ownProps) => {
  const module = getModuleByLocation(ownProps.location);
  return {
    setQueueFilterInput: debounce(sortInput => dispatch(setQueueFilterInput(module, sortInput)), 800),
    changeQueueSort: ({ sortInput, quickChange }) => dispatch(changeQueueSort(module, { sortInput, quickChange })),
    clearCompletedQueue: () => dispatch(clearCompletedQueue(module)),
    setShowPostAcquisitions: (shouldShow) => dispatch(setShowPostAcquisitions(shouldShow)),
  }
};

export default compose(
  withRouter,
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(QueueContainer)